import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import portfolioBuilder from "../../assets/products/portfolio-builder.jpg";
import { 
  FaUsers, 
  FaUserTie, 
  FaChartLine, 
  FaClipboardList, 
  FaFileAlt, 
  FaClock 
} from "react-icons/fa";

interface PortfolioBuilderPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const PortfolioBuilderPage = ({ product }: PortfolioBuilderPageProps) => {
  const features = [
    {
      icon: <FaUserTie />,
      title: "Expert Portfolio Review",
      description: "1-to-1 expert portfolio review with a top-10 ranked Ophthalmology trainee"
    },
    {
      icon: <FaUsers />,
      title: "Consultant Advice",
      description: "Advice from consultant ophthalmologists who are actively involved in recruitment"
    },
    {
      icon: <FaChartLine />,
      title: "Evidence Mapping",
      description: "Evidence mapping against scoring domains (audit, teaching, leadership, research)"
    },
    {
      icon: <FaClipboardList />,
      title: "Formatting Guidance",
      description: "Guidance on formatting, presentation, and high-impact content"
    },
    {
      icon: <FaFileAlt />,
      title: "Templates & Checklists",
      description: "Practical templates and checklists to organise your folder"
    },
    {
      icon: <FaClock />,
      title: "Full Day Sessions",
      description: "2 sessions across a full day for comprehensive portfolio development"
    }
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage">
        {/* Hero Section */}
        <div className="product-hero-section">
          <div className="product-hero-background">
            <div className="product-hero-left"></div>
            <div 
              className="product-hero-image-bg"
              style={{
                backgroundImage: `url(${portfolioBuilder})`
              }}
            ></div>
          </div>

          <div className="product-hero-overlay">
            <div className="site-container">
              <div className="product-hero-content">
                <h1>Portfolio Builder</h1>
                <p className="product-description">
                  For trainee/junior doctors to build a portfolio to get to the Ophthalmology interview stage. Comprehensive portfolio review to help you gain more points and improve your portfolio score. Get personalised support from top-ranked trainees and consultant ophthalmologists.
                </p>
                <div className="product-pricing">
                  <span className="price">£{product.price}</span>
                  <span className="length">/ {product.length}</span>
                  {product.comingSoon && (
                    <span className="coming-soon-pill">coming soon</span>
                  )}
                </div>
                {!product.comingSoon && (
                  <Button
                    text="Buy now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Image - Hidden on Desktop */}
        <div className="portfolio-builder-mobile-image" style={{
          display: "none",
          width: "100%",
          maxWidth: "450px",
          margin: "0 auto",
          padding: "0 20px"
        }}>
          <img 
            src={portfolioBuilder} 
            alt="Portfolio Builder"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
            }}
          />
        </div>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                Stand out in the highly competitive Ophthalmology ST recruitment process. Your evidence folder can be worth up to 50 points in national selection — every detail counts.
              </p>
              <p className="solution-text">
                <strong>Get the edge before the interview stage.</strong>
              </p>
            </section>

            {/* What You Get Section */}
            <section className="sales-section">
              <h2 style={{ fontSize: "2rem", fontWeight: "bold", color: "#0e4f99", marginBottom: "30px", textAlign: "center" }}>
                What You Get
              </h2>
              <p style={{ fontSize: "1.1rem", textAlign: "center", color: "#666", marginBottom: "40px", maxWidth: "800px", margin: "0 auto 40px" }}>
                This service is designed for trainee/junior doctors to build a portfolio to get to the Ophthalmology interview stage and gain more points to improve their portfolio score.
              </p>
              
              <div className="feature-grid" style={{ 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: "25px", 
                marginTop: "30px"
              }}>
                {features.map((feature, index) => (
                  <div key={index} className="feature-card" style={{ 
                    background: "white", 
                    padding: "25px", 
                    borderRadius: "12px", 
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start"
                  }} onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.12)";
                  }} onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
                  }}>
                    <div style={{
                      color: "#0e4f99",
                      fontSize: "2rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "60px",
                      height: "60px",
                      background: "rgba(14, 79, 153, 0.1)",
                      borderRadius: "12px",
                      marginBottom: "15px"
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "bold", 
                      color: "#0e4f99", 
                      marginBottom: "10px",
                      marginTop: 0
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{ 
                      fontSize: "0.95rem", 
                      color: "#666", 
                      lineHeight: "1.6",
                      margin: 0
                    }}>
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Limited Availability Notice */}
            <section className="sales-section intro-section" style={{ background: "#fff3cd", padding: "25px", borderRadius: "8px", border: "2px solid #ffc107" }}>
              <p className="lead-text" style={{ margin: 0, color: "#856404", fontWeight: "600" }}>
                <FaUsers style={{ marginRight: "10px", display: "inline" }} />
                Limited spots available — book early to secure your place
              </p>
              </section>

            {/* CTA Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Get Started Today</h3>
                <div className="pricing-amount" style={{ marginBottom: "15px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "8px", marginBottom: product.comingSoon ? "10px" : "0" }}>
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">/ {product.length}</span>
                  </div>
                  {product.comingSoon && (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <span className="coming-soon-pill">coming soon</span>
                    </div>
                  )}
                </div>
                <p style={{ marginBottom: "10px", color: "#666" }}>
                  2 sessions across a full day
                </p>
                <p style={{ marginBottom: "20px", color: "#666", fontSize: "0.95rem" }}>
                  Contact us to discuss your portfolio building needs and get started.
                </p>
                <div className="pricing-cta">
                  {!product.comingSoon && (
                    <Button
                      text="Buy now"
                      linkTo={`/sign-up?product=${product.id}`}
                      type="navy"
                    />
                  )}
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PortfolioBuilderPage;

